import { SelectField } from '@shared/components/select-field/select-field';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  inject,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { QRCodeComponent } from 'angularx-qrcode';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  lucideCopy,
  lucideExternalLink,
  lucideCheck,
  lucideFileText,
  lucideMapPin,
  lucideShieldAlert,
  lucideRefreshCw,
} from '@ng-icons/lucide';
import { HlmButtonDirective } from '@spartan-ng/helm/button';
import { HlmSwitchComponent } from '@spartan-ng/helm/switch';
import {
  Analysis,
  BUSINESS_STATUS_METADATA,
  FileOverviewItem,
} from '@shared/models/analysis.model';
import { AnalysisDetailStore } from '@features/analyses/store/analysis-detail.store';
import { AnalysisService } from '@shared/services/analysis.service';
import { ToastService } from '@shared/services/toast.service';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import { environment } from '@env/environment';

const debugLog = (...args: unknown[]) => {
  if (!environment.production) {
    console.log(...args);
  }
};

@Component({
  selector: 'app-analysis-customer-tab',
  standalone: true,
  imports: [SelectField, 
    CommonModule,
    FormsModule,
    QRCodeComponent,
    NgIconComponent,
    HlmButtonDirective,
    HlmSwitchComponent,
  ],
  providers: [
    provideIcons({
      lucideCopy,
      lucideExternalLink,
      lucideCheck,
      lucideFileText,
      lucideMapPin,
      lucideShieldAlert,
      lucideRefreshCw,
    }),
  ],
  templateUrl: './analysis-customer-tab.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalysisCustomerTab {
  private readonly store = inject(AnalysisDetailStore);
  private readonly analysisService = inject(AnalysisService);
  private readonly toastService = inject(ToastService);
  private readonly currentOrgStore = inject(CurrentOrganizationStore);

  analysis = input.required<Analysis>();
  canEdit = input(true);
  filesOverview = input<FileOverviewItem[]>([]);
  filesRequested = output<void>();

  readonly businessStatus = computed(() => {
    const status = this.analysis().businessStatus ?? 'pending';
    return BUSINESS_STATUS_METADATA[status];
  });

  readonly reportFile = computed(() =>
    this.filesOverview().find(
      (file) => file.exists && String(file.tag) === 'pdf_report',
    ),
  );

  /** Signal-based copy feedback — required for OnPush reactivity */
  readonly copied = signal(false);

  /** Loading state while the toggle API call is in-flight */
  readonly isToggling = signal(false);
  readonly linkError = signal<string | null>(null);
  readonly accessMonthOptions = [{ value: '6', label: '6 Monate' }, { value: '12', label: '12 Monate' }];
  publicAccessMonths = 6;

  /**
   * Resolved stable publicId for QR code links.
   * Populated from the analysis model if available (set on creation),
   * or lazily fetched from the backend on first render.
   */
  private readonly resolvedPublicId = signal<string | null>(null);
  private linkRequestInFlight = false;

  constructor() {
    // Resolve publicId from analysis signal or fetch from backend if missing
    effect(() => {
      const an = this.analysis();
      if (an.publicId) {
        this.resolvedPublicId.set(an.publicId);
        return;
      }
      if (!an.publicAccessEnabled) return;

      void this.resolvePublicLink(an);
    });
  }

  /** Public-facing URL using the stable publicId (QR-code safe) */
  publicLink = computed(() => {
    const id = this.resolvedPublicId();
    if (!id) return '';
    return `${window.location.origin}/public/analyses/${id}`;
  });

  async copyLink(): Promise<void> {
    const link = this.publicLink();
    if (!link || !this.analysis().publicAccessEnabled) return;
    try {
      await navigator.clipboard.writeText(link);
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 2000);
    } catch {
      this.toastService.error('Link konnte nicht kopiert werden');
    }
  }

  openLink(): void {
    const link = this.publicLink();
    if (!link || !this.analysis().publicAccessEnabled) return;
    window.open(link, '_blank', 'noopener,noreferrer');
  }

  retryPublicLink(): void {
    this.linkError.set(null);
    void this.resolvePublicLink(this.analysis());
  }

  async togglePublicAccess(enabled: boolean): Promise<void> {
    if (this.isToggling()) return;

    const an = this.analysis();
    const orgId = this.currentOrgStore.organization()?.id || an.organizationId;
    if (!orgId) return;

    // Optimistic update
    const previousValue = an.publicAccessEnabled;
    this.store.updateAnalysisOptimistically({ publicAccessEnabled: enabled });

    this.isToggling.set(true);
    try {
      const updated = await this.analysisService.updateAnalysis(
        orgId,
        an.analysisId,
        { publicAccessEnabled: enabled, ...(enabled && { publicAccessMonths: this.publicAccessMonths }) },
      );
      this.store.updateAnalysisOptimistically(updated);
      this.toastService.success(
        enabled
          ? 'Öffentlicher Link aktiviert'
          : 'Öffentlicher Link deaktiviert',
      );
    } catch (error) {
      // Rollback optimistic update on failure
      this.store.updateAnalysisOptimistically({
        publicAccessEnabled: previousValue,
      });
      this.toastService.error('Fehler beim Ändern des Status');
      debugLog('[AnalysisCustomerTab] togglePublicAccess failed', error);
    } finally {
      this.isToggling.set(false);
    }
  }

  private async resolvePublicLink(an: Analysis): Promise<void> {
    const orgId = this.currentOrgStore.organization()?.id || an.organizationId;
    if (!orgId || this.resolvedPublicId() || this.linkRequestInFlight) return;

    this.linkRequestInFlight = true;
    try {
      const result = await this.analysisService.createPublicLink(
        orgId,
        an.analysisId,
      );
      this.resolvedPublicId.set(result.publicId);
      this.linkError.set(null);
    } catch {
      this.linkError.set(
        'Der öffentliche Link konnte nicht geladen werden. Bitte versuchen Sie es erneut.',
      );
    } finally {
      this.linkRequestInFlight = false;
    }
  }
}
